import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    CalendarBlank,
    CaretDown,
    CaretUp,
    ChatCircle,
    CheckCircle,
    Lifebuoy,
    PlayCircle,
    Question,
    VideoCamera,
} from '@phosphor-icons/react';
import {useHelpDrawer} from '../help/HelpDrawerContext';
import {
    STEP_DATA_EXPLORER_REPORT,
    STEP_FIRST_ALERT,
    STEP_FIRST_CONTROL_LINE,
    STEP_SELECT_DASHBOARD,
} from '../help/useOnboardingProgress';
import controlLineOnboardingGif from '../../images/onboarding/control-line-onboarding.gif';
import './DashboardSetupOnboarding.scss';

const DASHBOARD_SETUP_STEPS = [
    {
        key: STEP_FIRST_CONTROL_LINE,
        labelKey: 'help.step_first_control_line',
        descriptionKey: 'help.step_first_control_line_description',
        ctaKey: 'dashboard.setup.open_help',
        media: controlLineOnboardingGif,
    },
    {
        key: STEP_SELECT_DASHBOARD,
        labelKey: 'help.step_select_dashboard',
        descriptionKey: 'help.step_select_dashboard_description',
        ctaKey: 'dashboard.setup.open_help',
        action: 'help',
    },
    {
        key: STEP_FIRST_ALERT,
        labelKey: 'help.step_first_alert',
        descriptionKey: 'help.step_first_alert_description',
        ctaKey: 'dashboard.setup.open_help',
        action: 'help',
    },
    {
        key: STEP_DATA_EXPLORER_REPORT,
        labelKey: 'help.step_data_explorer_report',
        descriptionKey: 'help.step_data_explorer_report_description',
        ctaKey: 'dashboard.setup.open_help',
        action: 'help',
    },
];

const RESOURCES = [
    {key: 'intro', labelKey: 'dashboard.setup.resource_intro', Icon: PlayCircle},
    {key: 'support', labelKey: 'dashboard.setup.resource_support', Icon: ChatCircle},
    {key: 'demo', labelKey: 'dashboard.setup.resource_demo', Icon: CalendarBlank},
    {key: 'help', labelKey: 'dashboard.setup.resource_help', Icon: Question},
    {key: 'guides', labelKey: 'dashboard.setup.resource_guides', Icon: VideoCamera},
];

export function DashboardSetupOnboarding() {
    const {t} = useTranslation();
    const {openDrawer} = useHelpDrawer();
    const [openStepKey, setOpenStepKey] = useState(DASHBOARD_SETUP_STEPS[0].key);

    const completedCount = 0;
    const totalCount = DASHBOARD_SETUP_STEPS.length;
    const progressPercent = useMemo(() => {
        return totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);
    }, [completedCount, totalCount]);

    const handleStepAction = (step) => {
        openDrawer('basic', {stepKey: step.key});
    };

    return (
        <div className="dashboard-setup-onboarding">
            <main className="dashboard-setup-onboarding__main">
                <header className="dashboard-setup-onboarding__header">
                    <h1>
                        <span aria-hidden="true">👋</span>
                        {t('dashboard.setup.greeting')}
                    </h1>
                    <div className="dashboard-setup-onboarding__progress" aria-label={t('dashboard.setup.progress', {completed: completedCount, total: totalCount})}>
                        <div className="dashboard-setup-onboarding__progress-track">
                            <span style={{width: `${progressPercent}%`}}/>
                        </div>
                        <strong>{t('dashboard.setup.progress', {completed: completedCount, total: totalCount})}</strong>
                    </div>
                </header>

                <ol className="dashboard-setup-onboarding__steps">
                    {DASHBOARD_SETUP_STEPS.map((step) => {
                        const isOpen = openStepKey === step.key;
                        const cardClass = 'dashboard-setup-onboarding__step'
                            + (isOpen ? ' -open' : '');
                        const ToggleIcon = isOpen ? CaretUp : CaretDown;

                        return (
                            <li key={step.key} className={cardClass}>
                                <button
                                    type="button"
                                    className="dashboard-setup-onboarding__step-header"
                                    onClick={() => setOpenStepKey(isOpen ? null : step.key)}
                                    aria-expanded={isOpen}
                                >
                                    <span className="dashboard-setup-onboarding__step-check">
                                        <CheckCircle size={18} weight="bold"/>
                                    </span>
                                    <span>{t(step.labelKey)}</span>
                                    <ToggleIcon size={18} weight="bold"/>
                                </button>

                                {isOpen && (
                                    <div className="dashboard-setup-onboarding__step-body">
                                        <div className="dashboard-setup-onboarding__step-copy">
                                            <p>{t(step.descriptionKey)}</p>
                                            <button
                                                type="button"
                                                className="dashboard-setup-onboarding__cta"
                                                onClick={() => handleStepAction(step)}
                                            >
                                                <Lifebuoy size={16} weight="bold"/>
                                                {t(step.ctaKey)}
                                            </button>
                                        </div>
                                        <div className="dashboard-setup-onboarding__media" aria-hidden="true">
                                            <img src={step.media || controlLineOnboardingGif} alt="" />
                                        </div>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ol>
            </main>

            <aside className="dashboard-setup-onboarding__resources">
                <h2>{t('dashboard.setup.resources_title')}</h2>
                <ul>
                    {RESOURCES.map(({key, labelKey, Icon}) => (
                        <li key={key}>
                            <button type="button">
                                <Icon size={18} weight="regular"/>
                                <span>{t(labelKey)}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            </aside>
        </div>
    );
}

export default DashboardSetupOnboarding;
